import { Component, HostListener, Inject, NgZone, OnInit } from '@angular/core';
import { environment } from '../environments/environment';
import { SwUpdate } from '@angular/service-worker';
import { ToastService } from '@hutsix/ngxh6';
import { take } from 'rxjs/operators';
import Bugsnag from '@bugsnag/js';
import { ActivatedRoute, ActivationEnd, NavigationEnd, Router } from '@angular/router';
import { ApiResponse, OfflineServiceInterface, ThemeServiceInterface, UserServiceInterface } from '@hutsix/ngxh6';
import { ApiServiceInterface } from '@hutsix/ngxh6';
import { UserInterface } from './_generated/api_interfaces/api/user.interface';
import { GoogleAnalyticsService } from './services/google-analytics/google-analytics.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent implements OnInit {
    public showRequests = !environment.production;
    public online: boolean = true;
    public hideNavbar: boolean = false;
    public padContent: boolean = false;

    /**
     * True while a Business Login holder is browsing the PUBLIC site.
     *
     * Not derived from padContent: /business gets that too, and a banner telling
     * someone they are in the public area while they are looking at their own business
     * dashboard is worse than no banner. Staff are excluded - a TLC account can carry
     * ROLE_BUSINESS, and the CRM is not somewhere to be nudged out of.
     */
    public businessUserOnPublicSite: boolean = false;

    @HostListener('window:beforeunload', ['$event'])
    handleBeforeUnload($event: BeforeUnloadEvent): any {
        if (this.user.offline_lock && this.user.offlineSyncing) {
            const confirmationMessage = 'Offline sync is running. Are you sure?';
            $event.returnValue = confirmationMessage;
            return confirmationMessage;
        }
    }

    @HostListener('window:unload', ['$event'])
    handleUnload($event: BeforeUnloadEvent): any {
        if (this.user.offline_lock && this.user.offlineSyncing) {
            localStorage.setItem('clearOffline', JSON.stringify(true));
        }
    }

    @HostListener('window:load', ['$event'])
    handleLoad($event: BeforeUnloadEvent): any {
        if (JSON.parse(localStorage.getItem('clearOffline')) === true) {
            localStorage.setItem('clearOffline', JSON.stringify(false));
            this.user.offline_lock = false;
        }
    }

    @HostListener('touchstart', ['$event']) onTouchStart(event: TouchEvent): void {
        // is not near edge of view, exit
        const x = event.touches[0].pageX;
        if (x > 10 && x < window.innerWidth - 10) return;

        // prevent swipe to navigate gesture
        event.preventDefault();
    }

    constructor(
        @Inject('ApiService') public api: ApiServiceInterface,
        @Inject('OfflineService') private offline: OfflineServiceInterface,
        @Inject('UserService') public user: UserServiceInterface,
        @Inject('ThemeService') public themeService: ThemeServiceInterface,
        private swUpdate: SwUpdate,
        private toastr: ToastService,
        private zone: NgZone,
        private router: Router,
        private route: ActivatedRoute,
        private ga: GoogleAnalyticsService,
    ) {
        // Migrated from the removed ngx-toastr ToastrModule.forRoot() config (shared.module.ts).
        // `positionClass: 'toast-top-center'` maps to `position`; `enableHtml: false` had no
        // instance equivalent and is the default, so it was dropped.
        this.toastr.position = 'top-center';
        this.toastr.preventDuplicates = true;
        this.toastr.countDuplicates = true;
        this.toastr.resetTimeoutOnDuplicate = true;
        this.toastr.newestOnTop = false;

        this.ga.init();
        if (this.swUpdate.isEnabled) {
            this.swUpdate.versionUpdates.subscribe(event => {
                if (event.type === 'VERSION_DETECTED') {
                    console.log('New update available');
                    this.toastr
                        .info('Click To Refresh', 'APP Updated!', {
                            disableTimeOut: true,
                        })
                        .onTap.pipe(take(1))
                        .subscribe(() => this.updateToLatest());
                }
            });
        }

        this.zone.runOutsideAngular(() => {
            setInterval(() => {
                this.checkForUpdate();
            }, 10000);
        });
    }

    ngOnInit(): void {
        let first = true;
        this.user.watch.subscribe(user => {
            if (!user.loggedIn && !first) this.user.reloadRoute('/login');
            if (
                user.loggedIn &&
                (first ||
                    (user.account.impersonating && user.account.user.email !== user.account.impersonating) ||
                    (!user.account.impersonating && user.account.user.email !== user.account.email))
            ) {
                // This should only run once.
                first = false;
                // update the user
                this.api
                    .get({ url: '/api/v1/user/current', useCache: false, displayErrors: false })
                    .subscribe((result: ReturnType<UserInterface['apiUserCurrent']>) => this.user.updateUserData(result.data));
                // track offline status
                this.offline.stateChange.subscribe(status => {
                    this.online = status;
                    if (status) {
                        this.offline.getOfflineQueue().subscribe(res => {
                            if (res.length) {
                                this.toastr
                                    .info('Click to sync offline data.', 'Connection detected!', {
                                        disableTimeOut: true,
                                    })
                                    .onTap.pipe(take(1))
                                    .subscribe(() => this.router.navigateByUrl('/user-admin/offline'));
                            }
                        });
                    }
                });
            }
        });

        Bugsnag.addOnError(event => {
            if (this.user.loggedIn) event.setUser(this.user.user.id, this.user.user.email, this.user.user.name);
        });

        // Standalone/auth screens hide the navbar. Logged-in app areas (crm, user-admin,
        // debug) keep it - their layout sits under the fixed navbar and uses it as the header.
        // Standalone/auth screens hide the navbar. So does the Business Login area: it
        // brings its own fixed-top bar AND its own offset (.business-wrapper pads by
        // --navbar-height-total), so the shell was giving it a second fixed navbar
        // underneath and a second helping of padding. It looked survivable only because
        // the business bar is later in the DOM and painted over the other one.
        const hideNavbarOn = ['account', 'login', 'forgot-password', 'reload', 'unauthorised', 'business'];
        // These areas render their own app-wrapper that already offsets under the navbar
        // (.top-bar), so the shell must NOT add its own top padding or it doubles up.
        const selfOffsetOn = ['crm', 'user-admin', 'debug'];
        this.router.events.subscribe(p => {
            if (p instanceof NavigationEnd) {
                const topSegment = p.urlAfterRedirects.split('?')[0].split('/').filter(Boolean)[0] ?? '';
                this.hideNavbar = hideNavbarOn.includes(topSegment);
                this.padContent = !this.hideNavbar && !selfOffsetOn.includes(topSegment);
                // hideNavbar already excludes /business and the auth screens; selfOffsetOn
                // excludes /crm and friends. What is left is the public site.
                this.businessUserOnPublicSite =
                    !this.hideNavbar &&
                    !selfOffsetOn.includes(topSegment) &&
                    this.user.isGranted(['ROLE_BUSINESS']) &&
                    !this.user.isGranted(['ROLE_ADMIN', 'ROLE_STAFF']);
            }
        });
    }

    private checkForUpdate(): void {
        if (this.swUpdate.isEnabled) {
            this.swUpdate
                .checkForUpdate()
                .then(() => {
                    // console.log('Checking for updates...');
                })
                .catch(err => {
                    console.error('Error when checking for update', err);
                });
        }
    }

    private updateToLatest(): void {
        console.log('Updating to latest version.');
        this.swUpdate.activateUpdate().then(() => document.location.reload());
    }
}
